/**
 * Session segmentation config: fetch which skin concerns are allowed (key === "yes")
 * so we can generate masks only for those and skip the rest.
 */
import { SESSION_APP_DATA_BASE } from '../config/sessionAppData';
import { fetchWithTimeout } from './fetchWithTimeout';

export const SEGMENTATION_API_BASE = SESSION_APP_DATA_BASE;

export function parseFeatureToggle(value) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    return v === 'yes' || v === 'true' || v === '1' || v === 'on';
  }
  return false;
}

export function toYesNoValue(value) {
  if (value == null) return null;
  return parseFeatureToggle(value) ? 'yes' : 'no';
}

/**
 * Parse segmentation API payload (same rules as SkinRoutinePage).
 */
export function parseSegmentationPayload(data) {
  const fields = Array.isArray(data?.fields) ? data.fields : [];
  const segmentationMap = data?.segmentation && typeof data.segmentation === 'object'
    ? data.segmentation
    : null;

  let concernsEnabled = segmentationMap?.['Skin Concerns'] != null
    ? parseFeatureToggle(segmentationMap['Skin Concerns'])
    : true;
  let conditionsEnabled = segmentationMap?.['Skin Conditions'] != null
    ? parseFeatureToggle(segmentationMap['Skin Conditions'])
    : true;
  let advancedCharts = segmentationMap?.['Advanced graphical charts'] != null
    ? parseFeatureToggle(segmentationMap['Advanced graphical charts'])
    : true;

  const allowed = new Set();
  fields.forEach((field) => {
    const label = String(field?.label || '').trim();
    if (label === 'Skin Concerns' && segmentationMap?.['Skin Concerns'] == null) {
      concernsEnabled = parseFeatureToggle(field?.key);
    }
    if (label === 'Skin Conditions' && segmentationMap?.['Skin Conditions'] == null) {
      conditionsEnabled = parseFeatureToggle(field?.key);
    }
    if (label === 'Advanced graphical charts' && segmentationMap?.['Advanced graphical charts'] == null) {
      advancedCharts = parseFeatureToggle(field?.key);
    }
    if (field?.key !== 'yes' || !Array.isArray(field?.options)) return;
    field.options.forEach((opt) => {
      const name = normalizeSegmentationOption(opt);
      if (name) allowed.add(name);
    });
  });

  const hasProductFlag = Object.prototype.hasOwnProperty.call(data || {}, 'product_suggestion')
    || Object.prototype.hasOwnProperty.call(data || {}, 'product');
  const productSuggestion = hasProductFlag
    ? parseFeatureToggle(data?.product_suggestion ?? data?.product)
    : true;
  const productActions = resolveSegmentationProductActions(data);

  const limit = data?.product_suggest_limit;
  const num = typeof limit === 'number' ? limit : parseInt(limit, 10);
  const productSuggestLimit = Number.isNaN(num) || num < 1 ? null : num;

  const mode = String(data?.ai_mode || '').trim().toLowerCase();
  const aiMode = mode === 'advanced' || mode === 'basic' ? mode : null;

  return {
    showConcerns: concernsEnabled,
    showConditions: conditionsEnabled,
    advancedCharts,
    productSuggestion,
    addToCart: productActions.addToCart,
    productLink: productActions.productLink,
    allowedNames: allowed.size > 0 ? allowed : (fields.length > 0 ? new Set() : null),
    aiGeneratedSummary: toYesNoValue(data?.ai_generated_summary),
    aiMode,
    productSuggestLimit,
  };
}

/** Product action flags from segmentation API (`add_to_cart`, `product_link`). */
export function resolveSegmentationProductActions(data) {
  return {
    addToCart: Object.prototype.hasOwnProperty.call(data || {}, 'add_to_cart')
      ? parseFeatureToggle(data.add_to_cart)
      : true,
    productLink: Object.prototype.hasOwnProperty.call(data || {}, 'product_link')
      ? parseFeatureToggle(data.product_link)
      : true,
  };
}

export function isValidProductUrl(url) {
  if (url == null || typeof url !== 'string') return false;
  const trimmed = url.trim();
  return trimmed.length > 0;
}

/**
 * Map API option text to our class_name / displayName (must match backend and SkinResultsPage).
 */
export function normalizeSegmentationOption(option) {
  if (!option || typeof option !== 'string') return null;
  const s = option.trim();
  if (s.toLowerCase().startsWith('uneven') || s.toLowerCase().includes('uneven skin') || s.toLowerCase().includes('uneven tone')) return 'Uneven Skintone';
  if (s.toLowerCase().startsWith('hydration')) return 'Hydration';
  if (s.toLowerCase().includes('smoothness')) return 'Smoothness';
  if (s.toLowerCase().startsWith('oxygen')) return 'Oxygen';
  if (s.toLowerCase().startsWith('dullness')) return 'Dullness';
  if (s === 'Wrinkles') return 'Wrinkles';
  if (s === 'Dry skin') return 'Dry skin';
  if (s === 'Oily skin') return 'Oily skin';
  if (s === 'Dark circle') return 'Dark circle';
  if (s === 'Acne') return 'Acne';
  if (s === 'Red spot') return 'Red spot';
  if (s === 'Pigmentation') return 'Pigmentation';
  if (s === 'Mole') return 'Mole';
  if (s === 'Dark spots') return 'Dark spots';
  if (s === 'Whiteheads') return 'Whiteheads';
  if (s === "Crow's feet") return "Crow's feet";
  if (s === 'Open pores') return 'Open Pores';
  if (s === 'Blackheads') return 'Blackheads';
  if (s === 'Clogged pores') return 'Clogged Pores';
  if (s === 'Eye Bags') return 'Eye Bags';
  if (s === 'Unknown') return 'Unknown';
  return s;
}

/**
 * Fetch segmentation config for session and return set of allowed condition names (key === "yes" only).
 * When key is "no" (e.g. "Skin Concerns" with options []), nothing is added to allowed, so we return
 * an empty set and the backend will skip all those concerns.
 * Returns null only if no token, request fails, or response has no fields array.
 * Returns empty Set when segmentation exists but no field has key "yes" (so backend must skip all).
 */
export async function fetchAllowedConditionNames(token) {
  if (!token) return null;
  try {
    const res = await fetchWithTimeout(
      `${SEGMENTATION_API_BASE}/${encodeURIComponent(token)}/segmentation`,
      { timeoutMs: 20000 }
    );
    if (!res.ok) return null;
    const data = await res.json().catch(() => ({}));
    const fields = data?.fields ?? data?.data?.fields;
    if (!Array.isArray(fields)) return null;
    const allowed = new Set();
    fields.forEach((field) => {
      if (field.key !== 'yes') return;
      const opts = field.options;
      if (!Array.isArray(opts)) return;
      opts.forEach((opt) => {
        const name = normalizeSegmentationOption(opt);
        if (name) allowed.add(name);
      });
    });
    return allowed;
  } catch {
    return null;
  }
}
